import { useEffect, useMemo, useState } from "react";
import { FiShield, FiSearch, FiRefreshCcw, FiUsers } from "react-icons/fi";
import backendApi from "../api/backendApi";
import { protectedTheme } from "../components/ui/protectedTheme";
import { formatMuqamiName } from "../components/adminAnalytics/analyticsUtils";

const ROLE_OPTIONS = ["ROLE_USER", "ROLE_USER_MAAL", "ROLE_ADMIN"];
const STATUS_OPTIONS = ["ACTIVE", "INACTIVE", "SUSPENDED"];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DEFAULT_FILTERS = {
  search: "",
  muqami: "",
  role: "",
  status: "",
  size: 20,
};

function getPageContent(pageData) {
  return Array.isArray(pageData?.content) ? pageData.content : [];
}

function formatRoleLabel(role = "") {
  if (role === "ROLE_USER_MAAL") return "Muqami Maal";
  if (role === "ROLE_ADMIN") return "Admin";
  if (role === "ROLE_USER") return "User";
  return role || "-";
}

function formatStatusLabel(status = "") {
  return status
    ? status
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-MY", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function rolePillClass(role) {
  if (role === "ROLE_ADMIN") return "bg-red-50 text-red-700 ring-red-100";
  if (role === "ROLE_USER_MAAL")
    return "bg-lime-100 text-emerald-900 ring-lime-200";
  return "bg-emerald-50 text-emerald-700 ring-emerald-100";
}

function statusPillClass(status) {
  if (status === "ACTIVE") return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  if (status === "SUSPENDED") return "bg-red-50 text-red-700 ring-red-100";
  return "bg-zinc-100 text-zinc-700 ring-zinc-200";
}

function getUpdateWarning(user, nextRole) {
  if (nextRole === "ROLE_ADMIN") {
    return "This grants national admin access, including admin analytics and user access management.";
  }
  if (user?.role === "ROLE_ADMIN" && nextRole !== "ROLE_ADMIN") {
    return "This removes admin access from this user. The backend will reject last-admin and self-demotion attempts.";
  }
  if (nextRole === "ROLE_USER_MAAL") {
    return "This user will keep normal user access and gain the scoped Muqami Dashboard.";
  }
  return "This user will have regular personal dashboard, payment, transaction, and profile access.";
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className={protectedTheme.label}>{label}</span>
    {children}
  </label>
);

const Pill = ({ children, className }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ring-1 ${className}`}
  >
    {children}
  </span>
);

const ConfirmRoleModal = ({
  state,
  selectedRole,
  onClose,
  onConfirm,
}) => {
  if (!state.open || !state.user) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-zinc-950/60 p-4 backdrop-blur">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-emerald-950 p-3 text-lime-200">
            <FiShield className="text-xl" />
          </div>
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-700">
              Confirm Role Change
            </p>
            <h2 className="mt-1 text-2xl font-black text-zinc-950">
              Update {state.user.name || state.user.email}
            </h2>
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-[#f6f3ea] p-4 text-sm text-zinc-700">
          <p>
            Change role from{" "}
            <strong>{formatRoleLabel(state.user.role)}</strong> to{" "}
            <strong>{formatRoleLabel(selectedRole)}</strong>.
          </p>
          <p className="mt-2 text-zinc-600">
            {getUpdateWarning(state.user, selectedRole)}
          </p>
        </div>

        {state.error ? (
          <div className="mt-4 rounded-2xl bg-red-50 p-3 text-sm font-semibold text-red-700">
            {state.error}
          </div>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className={protectedTheme.secondaryButton}
            disabled={state.submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={protectedTheme.accentButton}
            disabled={state.submitting}
            onClick={onConfirm}
          >
            {state.submitting ? "Updating..." : "Confirm Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserAccessManagementPage = () => {
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [muqamiOptions, setMuqamiOptions] = useState([]);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [confirmState, setConfirmState] = useState({
    open: false,
    user: null,
    submitting: false,
    error: "",
  });

  const users = getPageContent(pageData);
  const totalPages = Number(pageData?.totalPages ?? 0);
  const currentPage = Number(pageData?.number ?? page);
  const selectedConfirmRole = confirmState.user
    ? roleDrafts[confirmState.user.userId] || confirmState.user.role
    : "";

  useEffect(() => {
    let ignore = false;

    const loadMuqamis = async () => {
      try {
        const response = await backendApi.get("/muqamis");
        if (!ignore) {
          setMuqamiOptions(Array.isArray(response.data) ? response.data : []);
        }
      } catch {
        if (!ignore) setMuqamiOptions([]);
      }
    };

    loadMuqamis();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadUsers = async () => {
      setLoading(true);
      setError("");

      try {
        const params = {
          page,
          size: filters.size,
        };
        ["search", "muqami", "role", "status"].forEach((key) => {
          if (filters[key]) params[key] = filters[key];
        });

        const response = await backendApi.get("/admin/users/access", {
          params,
        });

        if (!ignore) {
          setPageData(response.data);
          const nextDrafts = {};
          getPageContent(response.data).forEach((user) => {
            nextDrafts[user.userId] = user.role;
          });
          setRoleDrafts(nextDrafts);
          setLoading(false);
        }
      } catch (requestError) {
        if (!ignore) {
          setError(
            requestError?.response?.data?.message ||
              requestError.message ||
              "Failed to load users."
          );
          setPageData(null);
          setLoading(false);
        }
      }
    };

    loadUsers();
    return () => {
      ignore = true;
    };
  }, [filters, page]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(filters.search || filters.muqami || filters.role || filters.status),
    [filters]
  );

  const updateDraftFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setFeedback("");
    setPage(0);
    setFilters(draftFilters);
  };

  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPage(0);
    setFeedback("");
  };

  const openConfirm = (user) => {
    const nextRole = roleDrafts[user.userId] || user.role;
    if (nextRole === user.role) return;
    setConfirmState({
      open: true,
      user,
      submitting: false,
      error: "",
    });
  };

  const closeConfirm = () => {
    setConfirmState((current) =>
      current.submitting
        ? current
        : {
            open: false,
            user: null,
            submitting: false,
            error: "",
          }
    );
  };

  const confirmRoleUpdate = async () => {
    if (!confirmState.user || !selectedConfirmRole) return;

    setConfirmState((current) => ({
      ...current,
      submitting: true,
      error: "",
    }));

    try {
      await backendApi.patch(
        `/admin/users/access/${confirmState.user.userId}/role`,
        {
          role: selectedConfirmRole,
          confirmAdminGrant: selectedConfirmRole === "ROLE_ADMIN",
        }
      );

      setFeedback(
        `${confirmState.user.email} was updated to ${formatRoleLabel(
          selectedConfirmRole
        )}.`
      );
      setConfirmState({
        open: false,
        user: null,
        submitting: false,
        error: "",
      });
      setFilters((current) => ({ ...current }));
    } catch (requestError) {
      setConfirmState((current) => ({
        ...current,
        submitting: false,
        error:
          requestError?.response?.data?.message ||
          requestError.message ||
          "Failed to update role.",
      }));
    }
  };

  const renderRoleControl = (user) => {
    const draftRole = roleDrafts[user.userId] || user.role;
    const changed = draftRole !== user.role;

    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          className={protectedTheme.input}
          value={draftRole}
          disabled={confirmState.submitting}
          onChange={(event) =>
            setRoleDrafts((current) => ({
              ...current,
              [user.userId]: event.target.value,
            }))
          }
        >
          {ROLE_OPTIONS.map((role) => (
            <option key={role} value={role}>
              {formatRoleLabel(role)}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={changed ? protectedTheme.accentButton : protectedTheme.secondaryButton}
          disabled={!changed || confirmState.submitting}
          onClick={() => openConfirm(user)}
        >
          Save
        </button>
      </div>
    );
  };

  return (
    <div className={protectedTheme.page}>
      <div className={protectedTheme.shell}>
        <div className="rounded-3xl bg-emerald-950 p-6 text-white shadow-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-lime-200">
                Admin Tools
              </p>
              <h1 className="mt-2 text-3xl font-black tracking-tight">
                User Access Management
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-emerald-100">
                Search users and manage dashboard/access roles. Role changes are
                validated by the backend before they are applied.
              </p>
            </div>
            <div className="rounded-3xl bg-white/10 p-4 text-lime-200">
              <FiUsers className="text-3xl" />
            </div>
          </div>
        </div>

        <form className={protectedTheme.card} onSubmit={handleSearch}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <Field label="Search">
              <input
                className={protectedTheme.input}
                value={draftFilters.search}
                placeholder="Name, email, AIMS, phone"
                onChange={(event) => updateDraftFilter("search", event.target.value)}
              />
            </Field>

            <Field label="Muqami">
              <select
                className={protectedTheme.input}
                value={draftFilters.muqami}
                onChange={(event) => updateDraftFilter("muqami", event.target.value)}
              >
                <option value="">All Muqami</option>
                {muqamiOptions.map((muqami) => (
                  <option key={muqami} value={muqami}>
                    {formatMuqamiName(muqami)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Role">
              <select
                className={protectedTheme.input}
                value={draftFilters.role}
                onChange={(event) => updateDraftFilter("role", event.target.value)}
              >
                <option value="">All Roles</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {formatRoleLabel(role)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Status">
              <select
                className={protectedTheme.input}
                value={draftFilters.status}
                onChange={(event) => updateDraftFilter("status", event.target.value)}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {formatStatusLabel(status)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Page Size">
              <select
                className={protectedTheme.input}
                value={draftFilters.size}
                onChange={(event) =>
                  updateDraftFilter("size", Number(event.target.value))
                }
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </Field>

            <div className="flex items-end gap-2">
              <button
                type="submit"
                className={`${protectedTheme.accentButton} inline-flex flex-1 items-center justify-center gap-2`}
              >
                <FiSearch />
                Search
              </button>
              <button
                type="button"
                className={`${protectedTheme.secondaryButton} inline-flex items-center justify-center gap-2`}
                onClick={handleReset}
              >
                <FiRefreshCcw />
                Reset
              </button>
            </div>
          </div>
        </form>

        {feedback ? (
          <div className="rounded-2xl bg-lime-100 p-4 text-sm font-semibold text-emerald-900">
            {feedback}
          </div>
        ) : null}

        <section className={protectedTheme.card}>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-black text-zinc-950">Users</h2>
              <p className="text-sm text-zinc-500">
                {loading
                  ? "Loading users..."
                  : `${Number(pageData?.totalElements ?? 0).toLocaleString(
                      "en-MY"
                    )} result${Number(pageData?.totalElements ?? 0) === 1 ? "" : "s"}${
                      hasActiveFilters ? " for current filters" : ""
                    }`}
              </p>
            </div>
            <div className="text-sm font-semibold text-zinc-500">
              Page {totalPages ? currentPage + 1 : 0} of {totalPages}
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl bg-red-50 p-4 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : null}

          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full text-sm">
              <thead className="bg-emerald-950 text-left text-white">
                <tr>
                  <th className="rounded-l-2xl px-4 py-3 font-bold">Name</th>
                  <th className="px-4 py-3 font-bold">Email</th>
                  <th className="px-4 py-3 font-bold">AIMS No</th>
                  <th className="px-4 py-3 font-bold">Muqami</th>
                  <th className="px-4 py-3 font-bold">Tanzeem</th>
                  <th className="px-4 py-3 font-bold">Current Role</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="rounded-r-2xl px-4 py-3 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-zinc-500">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-zinc-500">
                      No users found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.userId} className="border-b border-zinc-100">
                      <td className="px-4 py-4">
                        <div className="font-black text-zinc-900">
                          {user.name || "-"}
                        </div>
                        <div className="text-xs text-zinc-400">
                          Created {formatDate(user.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-zinc-700">{user.email || "-"}</td>
                      <td className="px-4 py-4 text-zinc-700">{user.aimsNo || "-"}</td>
                      <td className="px-4 py-4 text-zinc-700">
                        {formatMuqamiName(user.muqami)}
                      </td>
                      <td className="px-4 py-4 text-zinc-700">
                        {formatStatusLabel(user.tanzeem)}
                      </td>
                      <td className="px-4 py-4">
                        <Pill className={rolePillClass(user.role)}>
                          {formatRoleLabel(user.role)}
                        </Pill>
                      </td>
                      <td className="px-4 py-4">
                        <Pill className={statusPillClass(user.status)}>
                          {formatStatusLabel(user.status)}
                        </Pill>
                      </td>
                      <td className="px-4 py-4">{renderRoleControl(user)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 lg:hidden">
            {loading ? (
              <div className="rounded-2xl bg-zinc-50 p-4 text-sm text-zinc-500">
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div className="rounded-2xl bg-zinc-50 p-4 text-sm text-zinc-500">
                No users found for the selected filters.
              </div>
            ) : (
              users.map((user) => (
                <article
                  key={user.userId}
                  className="rounded-3xl border border-emerald-100 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-black text-zinc-950">
                        {user.name || "-"}
                      </h3>
                      <p className="text-sm text-zinc-500">{user.email || "-"}</p>
                    </div>
                    <Pill className={rolePillClass(user.role)}>
                      {formatRoleLabel(user.role)}
                    </Pill>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-400">
                        AIMS No
                      </p>
                      <p className="font-semibold text-zinc-800">
                        {user.aimsNo || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-400">
                        Muqami
                      </p>
                      <p className="font-semibold text-zinc-800">
                        {formatMuqamiName(user.muqami)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-400">
                        Tanzeem
                      </p>
                      <p className="font-semibold text-zinc-800">
                        {formatStatusLabel(user.tanzeem)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-400">
                        Status
                      </p>
                      <Pill className={statusPillClass(user.status)}>
                        {formatStatusLabel(user.status)}
                      </Pill>
                    </div>
                  </div>
                  <div className="mt-4">{renderRoleControl(user)}</div>
                </article>
              ))
            )}
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              className={protectedTheme.secondaryButton}
              disabled={loading || currentPage <= 0}
              onClick={() => setPage((value) => Math.max(value - 1, 0))}
            >
              Previous
            </button>
            <span className="text-center text-sm font-semibold text-zinc-500">
              Showing page {totalPages ? currentPage + 1 : 0} of {totalPages}
            </span>
            <button
              type="button"
              className={protectedTheme.secondaryButton}
              disabled={loading || !totalPages || currentPage >= totalPages - 1}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </button>
          </div>
        </section>
      </div>

      <ConfirmRoleModal
        state={confirmState}
        selectedRole={selectedConfirmRole}
        onClose={closeConfirm}
        onConfirm={confirmRoleUpdate}
      />
    </div>
  );
};

export default UserAccessManagementPage;
